import * as _ from "lodash";
import moment from "moment";
import { getDateWithTimezone } from "../../utils/FormatUtils.js";
import { getStatusMsg, isOneOffTask } from "./AnalysisTaskUtils.js";
import { filterValues, isTriggerFilter, filterValueGenerator, hasSideStream, filterValuesGenerator, fileNameGenerator } from "./AnalysisFilterUtils.js";
import { Trigger_Filter_Prefix, Trigger_Filter_Suffix } from "../../utils/FormatConstants.js";
const defaultZoneName = "moment.defaultZone.name";
function getLastCompletedTask(analysisStudy) {
  let allTasks = analysisStudy.TaskSchedule.Tasks || [];
  let allCompleted = allTasks.filter((task) => {
    return task.State === "Completed";
  });
  if (allCompleted.length > 0) {
    let lastCompleted = allCompleted[allCompleted.length - 1];
    return lastCompleted;
  } else {
    return null;
  }
}
function getUpcomingTask(analysisStudy, lastCompletedTask) {
  let tasks = analysisStudy.TaskSchedule.Tasks || [];
  if (lastCompletedTask !== null) {
    let lastCompletedIndex = tasks.indexOf(lastCompletedTask);
    if (lastCompletedIndex > -1)
      tasks = tasks.slice(lastCompletedIndex + 1);
  }
  let allUpcoming = tasks.filter((task) => {
    return task.State !== "Deprecated" && task.State !== "Cancelled";
  });
  if (allUpcoming.length > 0) {
    return allUpcoming[0];
  } else {
    return null;
  }
}
function getUpcomingTaskInfo(analysisStudy, lastCompletedTask) {
  if (analysisStudy.IsActive) {
    let upcomingTask = getUpcomingTask(analysisStudy, lastCompletedTask);
    if (typeof upcomingTask !== "undefined" && upcomingTask !== null) {
      let scorecardName = getTaskName(analysisStudy, upcomingTask);
      return "Upcoming: " + scorecardName + " Scorecard";
    } else {
      return "";
    }
  } else {
    return "";
  }
}
function getTaskNameWithScorecardPurpose(analysisStudy, analysisTask) {
  if (!analysisTask) {
    return "";
  }
  var taskName = getTaskName(analysisStudy, analysisTask);
  if (!!analysisTask.ExecutionConfigurationOverrides && !!analysisTask.ExecutionConfigurationOverrides.ScorecardPurposeUsedForPrioritization && analysisTask.ExecutionConfigurationOverrides.ScorecardPurposeUsedForPrioritization !== "Unknown") {
    taskName += `
(${analysisTask.ExecutionConfigurationOverrides.ScorecardPurposeUsedForPrioritization})`;
  }
  return taskName;
}
function getTaskName(analysisStudy, analysisTask) {
  var _a, _b;
  if (!analysisTask) {
    return "";
  }
  let start = analysisTask.DateRange.Start;
  let end = analysisTask.DateRange.End;
  let unit = analysisTask.DateRange.Unit;
  let taskName;
  if (start === null && end === 0 && unit === "Day") {
    taskName = "Partial Day";
  } else if (start === null && unit === "Minute") {
    if (analysisTask.ComputedAbsoluteAnalysisRange.End) {
      var startDate = getDateWithTimezone((_b = (_a = analysisTask.ComputedAbsoluteAnalysisRange) == null ? void 0 : _a.Start) != null ? _b : "", defaultZoneName);
      var endDate = getDateWithTimezone(analysisTask.ComputedAbsoluteAnalysisRange.End, defaultZoneName);
      return `0 - ${moment.duration(endDate.diff(startDate)).asMinutes()} mins`;
    } else {
      taskName = "First ";
      if (end && end > 0) {
        taskName += "+" + end + " mins";
      }
    }
  } else if (start === null && end === null) {
    taskName = isRetrospective(analysisStudy) ? analysisStudy.Scope.AnalysisType : "One-Off";
  } else if (start === 0 && end === null) {
    taskName = "Final";
  } else if ((start || start === 0) && end) {
    taskName = `${unit} ${start.toString()}-${end.toString()}`;
  } else {
    taskName = "";
  }
  return taskName;
}
function getUpcomingTaskState(analysisStudy, lastCompletedTask) {
  if (analysisStudy.IsActive) {
    let upcomingTask = getUpcomingTask(analysisStudy, lastCompletedTask);
    if (typeof upcomingTask !== "undefined" && upcomingTask !== null) {
      if (upcomingTask.State === "NotStarted" && upcomingTask.EstimatedArrival && upcomingTask.EstimatedArrival.EstimatedArrivalTime) {
        return upcomingTask.EstimatedArrival.EstimatedArrivalTime;
      } else {
        return "Status: " + getStatusMsg(upcomingTask);
      }
    } else {
      return "";
    }
  } else {
    return "";
  }
}
function isRetrospective(analysisStudy) {
  return _.some(["SeedFinder", "RetrospectiveAA", "AAStream"], (t) => t === analysisStudy.Scope.AnalysisType);
}
function getTriggerFilterType(analysisStudy) {
  if (!analysisStudy) {
    return null;
  }
  var triggerFilter = filterValues(analysisStudy.Scope.Filters, isTriggerFilter);
  if (triggerFilter.length > 0) {
    return triggerFilter[0].Operator.replace(Trigger_Filter_Prefix, "").replace(
      Trigger_Filter_Suffix,
      ""
    );
  }
  return null;
}
function getTriggerFilterName(analysisStudy) {
  if (!analysisStudy) {
    return null;
  }
  let triggerFilters = filterValues(
    analysisStudy.Scope.Filters,
    isTriggerFilter,
    // AnalysisFilterUtils.filterDimensionGenerator
    // there was a bug in the Legacy implementation where filterDimensionGenerator
    // was not actually supplied (even though it looked liked it shoule, it was undefined)
    null
  );
  let result = "";
  for (let triggerFilter of triggerFilters) {
    result += triggerFilter.Dimension;
  }
  return result;
}
function getTriggerFilterExpression(analysisStudy) {
  return filterValues(
    analysisStudy.Scope.Filters,
    isTriggerFilter,
    filterValueGenerator
  );
}
function getSideStreamInfo(analysisStudy) {
  let triggerFilters = filterValues(analysisStudy.Scope.Filters, hasSideStream);
  if (triggerFilters.length > 0) {
    return {
      Location: triggerFilters[0].TriggerFilterSideStreamInfo.Location,
      JoinColumnPairs: triggerFilters[0].TriggerFilterSideStreamInfo.JoinColumnPairs,
      CustomParameters: triggerFilters[0].TriggerFilterSideStreamInfo.CustomParameters,
      AvailabilityStream: triggerFilters[0].TriggerFilterSideStreamInfo.AvailabilityStream
    };
  }
  return null;
}
function getVertical(analysisStudy) {
  return filterValues(
    analysisStudy.Scope.Filters,
    (x) => {
      return x.Dimension.toLowerCase() === "sv";
    },
    filterValuesGenerator
  );
}
function getFlightsInfo(analysisStudy) {
  let treatmentFlights = _.filter(analysisStudy.Scope.Flights, (f) => !f.IsControl).map((f) => `${f.FlightName} (T)`);
  let controlFlights = _.filter(analysisStudy.Scope.Flights, (f) => f.IsControl).map((f) => `${f.FlightName} (C)`);
  return [...treatmentFlights, ...controlFlights].join(", ");
}
function getMarkets(analysisStudy) {
  return filterValues(
    analysisStudy.Scope.Filters,
    (x) => {
      return x.Dimension.toLowerCase() === "mkt";
    },
    filterValuesGenerator
  );
}
function getResourceFiles(analysisStudy) {
  return filterValues(
    analysisStudy.Scope.SupportFiles,
    (x) => {
      return x.FileType === "ScopeResource";
    },
    fileNameGenerator
  );
}
function getReferenceFiles(analysisStudy) {
  return filterValues(
    analysisStudy.Scope.SupportFiles,
    (x) => {
      return x.FileType === "ScopeReference";
    },
    fileNameGenerator
  );
}
function hasOneOffTask(analysisStudy) {
  var oneOffs = analysisStudy.TaskSchedule.Tasks.filter((task) => {
    return isOneOffTask(task);
  });
  return oneOffs.length > 0;
}
export {
  getFlightsInfo,
  getLastCompletedTask,
  getMarkets,
  getReferenceFiles,
  getResourceFiles,
  getSideStreamInfo,
  getTaskName,
  getTaskNameWithScorecardPurpose,
  getTriggerFilterExpression,
  getTriggerFilterName,
  getTriggerFilterType,
  getUpcomingTask,
  getUpcomingTaskInfo,
  getUpcomingTaskState,
  getVertical,
  hasOneOffTask
};
//# sourceMappingURL=AnalysisStudyBctUtils.js.map
